import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { Error } from "../../../../../../../models/error";
import { Filters, Searches, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import { TaskType } from "../../../../../../../models/task.type";
import TaskTypeService from "../../../../../../../services/task.type.service";
import { useAppState } from "../../../../../../../context/StateContext";

const useActions = () => {
  const { currentUser } = useAppState();

  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [responseError, setResponseError] = useState<Error>();

  const { refetch, isFetching } = useQuery(
    ["task.type.list", page, pageSize, filters, searches, sorting],
    () =>
      TaskTypeService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTaskTypes(response.data);
      },
    }
  );

  const { refetch: getTaskTypeTotal } = useQuery(
    ["task.type.total", filters, searches],
    () =>
      TaskTypeService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: createTaskType, isLoading: isCreating } = useMutation(
    ["create.task.type"],
    (taskType: TaskType) => TaskTypeService.create(taskType),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getTaskTypeTotal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          setHasErrors(true);
          setResponseError(error.response.data);
        }
      },
    }
  );

  const { mutate: updateTaskType, isLoading: isUpdating } = useMutation(
    ["update.task.type"],
    ({ id, taskType }: { id: number; taskType: TaskType }) => TaskTypeService.update(id, taskType),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          setHasErrors(true);
          setResponseError(error.response.data);
        }
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    taskTypes,
    hasErrors,
    responseError,
    setResponseError,
    page,
    pageSize,
    total,
    isLoading: isFetching || isCreating || isUpdating,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createTaskType,
    updateTaskType,
  };
};

export default useActions;
